import {
	ItemType,
	TicketItem,
	TicketDetail,
	TicketDiscount,
	TableDetailForOrder,
	TicketDetailForOrder,
	FloorInfo,
	TicketType,
	RestaurantInfo,
	Modifier,
	DeliverySetting,
} from "@royalty/model";
import { PosHelper } from "./pos.helper";
import { isBuffer } from "util";

export class TicketHelper {
	public static allowDiscount(
		itemSelected: boolean,
		itemType: ItemType,
		orderItem: TicketItem
	) {
		if (orderItem) {
			if (orderItem.canDiscount)
				return itemSelected && itemType === ItemType.menu_item;
			return false;
		}
		return false;
	}

	public static allowDelete(
		itemSelected: boolean,
		itemType: ItemType,
		orderItem: TicketItem
	) {
		if (orderItem) {
			if (itemSelected) {
				if (
					itemType === ItemType.item_discount ||
					itemType === ItemType.item_special
				) {
					return true;
				} else {
					// If child item on set menu then return false
					if (itemType === ItemType.menu_item) {
						if (orderItem.parentSetMenuKey !== null) {
							if (orderItem.isChildren) return false;
						}
					}
					return !orderItem.isPrintedToKitchen;
				}
			}
			return false;
		}
		return false;
	}

	public static allowUpdateQuantity(
		itemSelected: boolean,
		itemType: ItemType,
		orderItem: TicketItem
	) {
		if (orderItem) {
			if (itemSelected) {
				if (
					itemType === ItemType.item_discount ||
					itemType === ItemType.item_special
				) {
					return true;
				} else {
					// If child item on set menu then return false
					if (itemType === ItemType.menu_item) {
						if (orderItem.parentSetMenuKey !== null) {
							if (orderItem.isChildren) return false;
						}
					}
					return !orderItem.isPrintedToKitchen;
				}
			} else {
				return false;
			}
		}
		return false;
	}

	public static allowUpdatePrice(itemSelected: boolean) {
		return itemSelected;
	}

	public static allowTransfer(itemSelected: boolean, orderItem: TicketItem) {
		if (orderItem) {
			if (itemSelected) {
				if (orderItem.isPrintedToKitchen) {
					// If child item on set menu then return false
					if (orderItem.parentSetMenuKey !== null) {
						if (orderItem.isChildren) return false;
					}
				}
				return orderItem.isPrintedToKitchen;
			}
			return false;
		}
		return false;
	}

	public static allowVoid(
		itemSelected: boolean,
		itemType: ItemType,
		orderItem: TicketItem
	) {
		if (orderItem) {
			if (itemSelected) {
				if (orderItem.isPrintedToKitchen) {
					// If child item on set menu then return false
					if (itemType === ItemType.menu_item) {
						if (orderItem.parentSetMenuKey !== null) {
							if (orderItem.isChildren) return false;
						}
					}
					return itemType === ItemType.menu_item;
				}
			} else {
				return false;
			}
		}
		return false;
	}

	private static calculatePercentServiceFees(
		totalServiceFee: number,
		serviceChargePercentage: number
	): number {
		if (totalServiceFee)
			return Math.ceil((totalServiceFee * serviceChargePercentage) / 100);
		return 0;
	}

	public static calculateTotalBill(
		ticketDetail: TicketDetail,
		restaurant: RestaurantInfo = null,
		deliverySetting: DeliverySetting
	): TicketDetail {
		if (ticketDetail && PosHelper.isArrayWithLength(ticketDetail.items)) {
			ticketDetail = TicketHelper.calculateTicketMoney(
				ticketDetail,
				restaurant,
				deliverySetting
			);
		}
		return ticketDetail;
	}

	private static calculateBill(
		ticketDetail: TicketDetail,
		restaurant: RestaurantInfo = null,
		deliverySetting: DeliverySetting
	): TicketDetail {
		try {
			let ticketTotalForDisplay: number = 0;
			let ticketSubTotal: number = 0;
			let ticketTotalDiscount: number = 0;
			let ticketTotalServiceFee: number = 0;
			let ticketTotalTax: number = 0;
			let serviceChargePercentage = restaurant
				? restaurant.serviceChargePercentage
				: 5;
			if (
				ticketDetail.ticketType === TicketType.TAKE_OUT ||
				ticketDetail.ticketType === TicketType.DELIVERY
			) {
				serviceChargePercentage = 0;
			}
			const defaultTaxRate = restaurant ? restaurant.defaultTaxRate : 10;
			const {
				isPercentDiscount,
				valueDiscount,
			}: {
				isPercentDiscount: boolean;
				valueDiscount: number;
			} = this.getDiscount(ticketDetail);
			ticketDetail.items.forEach((item) => {
				const {
					isNoTax,
					isNoServiceFee,
					isNett,
				}: {
					isNoTax: boolean;
					isNoServiceFee: boolean;
					isNett: boolean;
				} = this.hasInCludeSpecials(item);

				// Calculate sub total bill
				var itemSubTotal =
					TicketHelper.calculateItemSubTotalWithinCustomDiscount(
						item
					);
				//re-set item.price
				var taxRate = item.tax ? item.tax.rate : defaultTaxRate;
				var itemExcludeTax = taxRate == 8 ? 1.08 : 1.1;

				const totalPriceItemAndModifiers =
					this.calculateTotalPriceAndModifiers(item);
				ticketTotalForDisplay += totalPriceItemAndModifiers;
				let totalDiscountItemWithoutCustomDiscount: number =
					TicketHelper.getItemDiscountWithoutCustomDiscount(
						ticketDetail,
						item,
						isPercentDiscount,
						totalPriceItemAndModifiers,
						valueDiscount
					);
				if (!isPercentDiscount) {
					totalDiscountItemWithoutCustomDiscount = 0;
				}

				if (isNoServiceFee == false) {
					const totalItemServiceCharge =
						this.calculatePercentServiceFees(
							totalPriceItemAndModifiers -
							totalDiscountItemWithoutCustomDiscount,
							serviceChargePercentage
						);
					item.serviceCharge = totalItemServiceCharge;
					// Calculate tax
					if (ticketDetail.taxExcluded) {
						ticketTotalTax = 0;
					} else {
						let totalForCalculateTax =
							totalPriceItemAndModifiers -
							totalDiscountItemWithoutCustomDiscount;
						if (!isNoServiceFee) {
							totalForCalculateTax += totalItemServiceCharge;
						}
						if (!isNoTax) {
							// let taxRate =
							// 	item.tax && item.tax.rate
							// 		? item.tax.rate
							// 		: defaultTaxRate;
							let itemTax = Math.ceil(
								(totalForCalculateTax * defaultTaxRate) / 100
							);
							//HERO START ADD 12/02/2022
							itemTax = Math.ceil(
								(totalForCalculateTax *
									(item.tax
										? item.tax.rate
										: defaultTaxRate)) /
								100
							);
							item.taxAmount = itemTax;
							//HERO END ADD 12/02/2022
							ticketTotalTax += itemTax;
						}
					}
				} else {
					if (ticketDetail.ticketType != TicketType.TAKE_OUT)
						itemSubTotal = itemSubTotal / itemExcludeTax;
					item.serviceCharge = 0;
					item.taxAmount = 0;
					item.discount = 0;
				}

				if (ticketDetail.ticketType == TicketType.TAKE_OUT) {
					//itemSubTotal = itemSubTotal / itemExcludeTax;
					item.serviceCharge = 0;
					item.discount = 0;
				}

				item.subTotal = itemSubTotal;
				ticketSubTotal += itemSubTotal;
				ticketTotalServiceFee += item.serviceCharge;

				// Calculate total discount bill
				if (item.canDiscount) {
					if (item.discount) {
						// We don't calculate with item already have discount
					} else {
						ticketTotalDiscount += 0.0;
					}
				}
			});

			ticketTotalDiscount = isPercentDiscount
				? (ticketTotalDiscount * valueDiscount) / 100
				: valueDiscount;

			if (!isPercentDiscount) {
				ticketTotalServiceFee = this.calculatePercentServiceFees(
					ticketSubTotal - ticketTotalDiscount,
					serviceChargePercentage
				);
				// Need ignore the tax of cash discount & tax of service fee
				const taxCashDiscount: number = Math.ceil(
					(valueDiscount * defaultTaxRate) / 100
				);
				const taxServiceFee: number = this.calculatePercentServiceFees(
					taxCashDiscount,
					serviceChargePercentage
				);
				ticketTotalTax =
					ticketTotalTax - taxCashDiscount - taxServiceFee;
			}
			if (
				ticketDetail.ticketType === TicketType.TAKE_OUT ||
				ticketDetail.ticketType === TicketType.DELIVERY
			) {
				ticketTotalServiceFee = 0;
			}

			ticketDetail.subTotal = ticketSubTotal;
			ticketDetail.subToTalForDisplaying = ticketSubTotal;
			ticketDetail.serviceCharge = ticketTotalServiceFee;
			ticketDetail.totalDiscountBill = ticketTotalDiscount;
			ticketDetail.totalDiscount = ticketTotalDiscount;
			// Calculate delivery fee if yes
			ticketDetail.deliveryCharge = this.calculateDeliveryFee(
				ticketDetail,
				deliverySetting
			);
			const taxDeliveryCharge: number = Math.ceil(
				(ticketDetail.deliveryCharge * defaultTaxRate) / 100
			);

			var subtotal_8 = 0;
			var subtotal_10 = 0;
			var serviceChargeFee_8 = 0;
			var serviceChargeFee_10 = 0;
			var discount_8 = 0;
			var discount_10 = 0;
			for (var i = 0; i < ticketDetail.items.length; i++) {
				let e = ticketDetail.items[i];
				if ((e.tax.rate = 8)) {
					subtotal_8 += e.subTotal;
					serviceChargeFee_8 += e.serviceCharge;
					discount_8 += e.discount;
				} else if ((e.tax.rate = 10)) {
					subtotal_10 += e.subTotal;
					serviceChargeFee_10 += e.serviceCharge;
					discount_10 += e.discount;
				}
			}
			console.log(subtotal_8, discount_8, serviceChargeFee_8);
			console.log(subtotal_10, discount_10, serviceChargeFee_10);
			var vat_8 = (subtotal_8 - discount_8 + serviceChargeFee_8) * 0.08;
			var vat_10 =
				(subtotal_10 - discount_10 + serviceChargeFee_10) * 0.1;

			ticketDetail.totalTax = vat_8 + vat_10;

			const totalPrice =
				ticketSubTotal -
				ticketDetail.totalDiscountBill +
				ticketDetail.serviceCharge +
				ticketDetail.deliveryCharge +
				ticketDetail.totalTax;
			ticketDetail.totalPrice = totalPrice;
		} catch (e) {
			console.log(e);
		}
		return ticketDetail;
	}

	/*
	New calculate money of ticket bill
	*/
	private static calculateTicketMoney(
		ticketDetail: TicketDetail,
		restaurant: RestaurantInfo = null,
		deliverySetting: DeliverySetting
	): TicketDetail {
		try {

			let _total_8 = 0;
			let _total_10 = 0;
			let _discount_8 = 0;
			let _discount_10 = 0;
			let _discount_amount = 0;
			let _discount_amount_8 = 0;
			let _discount_amount_10 = 0;

			let _service_charge_8 = 0;
			let _service_charge_10 = 0;

			let serviceChargePercentage = restaurant
				? restaurant.serviceChargePercentage
				: 5;
			if (
				ticketDetail.ticketType === TicketType.TAKE_OUT ||
				ticketDetail.ticketType === TicketType.DELIVERY
			) {
				serviceChargePercentage = 0;
			}
			const defaultTaxRate = restaurant ? restaurant.defaultTaxRate : 10;
			const {
				isPercentDiscount,
				valueDiscount,
			}: {
				isPercentDiscount: boolean;
				valueDiscount: number;
			} = this.getDiscount(ticketDetail);

			for (let index = 0; index < ticketDetail.items.length; index++) {
				let item = ticketDetail.items[index];
				const {
					isNoTax,
					isNoServiceFee,
					isNett,
				}: {
					isNoTax: boolean;
					isNoServiceFee: boolean;
					isNett: boolean;
				} = this.hasInCludeSpecials(item);

				if (
					ticketDetail.ticketType != TicketType.TAKE_OUT &&
					!isNett &&
					!isNoServiceFee
				) {
					//Discount
					TicketHelper.checkCanDiscountByDiscountNew(
						ticketDetail.discounts,
						item
					);
				}

				var taxRate = item.tax ? item.tax.rate : defaultTaxRate;
				if (item.menuItem) taxRate = item.menuItem.tax.rate;

				var _item_price = item.price;
				var _item_quantity = item.quantity;
				var _item_subtotal = parseFloat(
					(_item_price * _item_quantity).toFixed(2)
				);

				let _itemVatRate = item.tax.rate == 8 ? 1.08 : 1.1;
				if (isNoServiceFee == false) {
					item.serviceCharge =
						parseFloat(
							(
								_item_price * _item_quantity -
								item.discount
							).toFixed(2)
						) *
						(serviceChargePercentage / 100);

					if (isNoTax == false) {
						item.tax.rate = taxRate;
						item.taxAmount =
							(_item_price * _item_quantity +
								item.serviceCharge) *
							(taxRate / 100);
					}
				} else {
					if (item.isPrintedToKitchen == false)
						_item_subtotal = _item_subtotal / _itemVatRate;

					item.serviceCharge = 0;
					item.discount = 0;

					if (ticketDetail.ticketType == TicketType.TAKE_OUT) {
						if (isNoTax == false) {
							item.tax.rate = taxRate;
							item.taxAmount =
								(_item_price * _item_quantity +
									item.serviceCharge) *
								(taxRate / 100);
						}
					}
				}
				if (ticketDetail.ticketType == TicketType.TAKE_OUT) {
					item.serviceCharge = 0;
					item.discount = 0;
				}

				let totalPriceModifiers: number = 0;
				if (PosHelper.isArrayWithLength(item.modifiers)) {
					item.modifiers.forEach((modifier) => {
						modifier.subTotal = 0;
						if (modifier.price && modifier.quantity) {
							modifier.subTotal =
								modifier.price * modifier.quantity;
							totalPriceModifiers += modifier.subTotal;
						}
					});
				}

				item.subTotal = _item_subtotal + totalPriceModifiers;
				item.totalPrice =
					item.subTotal -
					item.discount +
					item.serviceCharge +
					item.taxAmount;
			}

			//hậu xử lý
			//tính toán giá trị discount, netAmount cho ticket

			for (let index = 0; index < ticketDetail.items.length; index++) {
				let item = ticketDetail.items[index];

				if (item.tax.rate == 8) {
					_total_8 += item.subTotal;
					_discount_8 += item.discount;
					//_service_charge_8 += item.serviceCharge;
				} else if (item.tax.rate == 10) {
					_total_10 += item.subTotal;
					_discount_10 += item.discount;
					//_service_charge_10 += item.serviceCharge;
				}
			}

			//xử lý amount discount
			if (ticketDetail.discounts.some(x => x.isPercent === false)) {
				for (const discount of ticketDetail.discounts) {
					if (!discount.isPercent) {
						_discount_amount += discount.discountAmount;
					}
				}
			}

			if (_discount_8 > ticketDetail.totalDiscount)
				ticketDetail.totalDiscount = _discount_8;

			//init discount 8
			//xu ly neu tong discount lon hon tong 8
			_discount_amount_8 = _discount_amount;
			if (_discount_8 + _discount_10 + _discount_amount >= _total_8) {
				if (_discount_8 > 0 || _discount_10 > 0) {
					_discount_amount_8 = _total_8 - _discount_8;
					_discount_amount_10 = _discount_amount_10 - _discount_amount_8;
				} else {
					_discount_amount_10 = _discount_amount - _total_8;
					_discount_amount_8 = _total_8;
				}
			}
			_service_charge_8 = (_total_8) * 0.05;
			_service_charge_8 = (_service_charge_8 < 0 || ticketDetail.ticketType === TicketType.TAKE_OUT ||
				ticketDetail.ticketType === TicketType.DELIVERY) ? 0 : _service_charge_8;
			_service_charge_10 = (_total_10) * 0.05;
			_service_charge_10 = (_service_charge_10 < 0 || ticketDetail.ticketType === TicketType.TAKE_OUT ||
				ticketDetail.ticketType === TicketType.DELIVERY) ? 0 : _service_charge_10;
			;

			//apply from 03/10/2025
			if (_service_charge_10 > 0) {
				_service_charge_8 += _service_charge_10;
				_service_charge_10 = 0;
			}


			let _vat_8 = (_total_8 - _discount_8 - _discount_amount_8 + _service_charge_8) * 0.08;
			let _vat_10 = (_total_10 - _discount_10 - _discount_amount_10 + _service_charge_10) * 0.1;

			ticketDetail.totalTax = _vat_8 + _vat_10;
			ticketDetail.subTotal = _total_8 + _total_10;
			ticketDetail.subToTalForDisplaying = _total_8 + _total_10;

			ticketDetail.serviceCharge = _service_charge_8 + _service_charge_10;
			ticketDetail.totalDiscount = _discount_8 + _discount_10 + _discount_amount_8 + _discount_amount_10;

			ticketDetail.totalPrice =
				ticketDetail.subTotal -
				ticketDetail.totalDiscount +
				ticketDetail.serviceCharge +
				ticketDetail.totalTax;
			var mathTotalPrice = Math.ceil(ticketDetail.totalPrice);
			ticketDetail.roundingVn = mathTotalPrice - ticketDetail.totalPrice;
			ticketDetail.totalPrice = mathTotalPrice;
		} catch (error) {
			console.log(error);
		}
		return ticketDetail;
	}

	private static calcTicketItemByDiscount() { }

	private static calculateDeliveryFee(
		ticketDetail: TicketDetail,
		deliverySetting: DeliverySetting
	): number {
		let deliveryCharge = 0;
		if (ticketDetail.ticketType === TicketType.DELIVERY) {
			deliveryCharge = deliverySetting.deliveryFee;
			if (ticketDetail.id > 0) {
				if (ticketDetail.deliveryChargeDb) {
					deliveryCharge = ticketDetail.deliveryChargeDb;
				}
			}
			if (deliverySetting.freeDeliveryApplyFromAmount) {
				const subTotalForApplyDelivery =
					ticketDetail.subTotal - ticketDetail.totalDiscountBill;
				if (
					subTotalForApplyDelivery >=
					deliverySetting.freeDeliveryApplyFromAmount
				) {
					deliveryCharge = 0;
				}
			}
		}
		return deliveryCharge;
	}

	private static calculateItemSubTotalWithinCustomDiscount(item: TicketItem) {
		const itemPrice = item.price ? item.price : 0;
		const itemQuatity = item.quantity ? item.quantity : 0;
		item.subTotal = itemPrice * itemQuatity;
		return TicketHelper.calculateSubTotalInCludeDiscountAndModifiers(item);
	}

	private static getItemDiscountWithoutCustomDiscount(
		ticketDetail: TicketDetail,
		item: TicketItem,
		isPercentDiscount: boolean,
		itemSubTotalAndModifier: number,
		valueDiscount: number
	) {
		let itemDiscount: number = 0;
		valueDiscount = this.checkCanDiscountByDiscountNew(
			ticketDetail.discounts,
			item
		);
		if (valueDiscount == undefined) valueDiscount = 0;
		if (item.canDiscount) {
			itemDiscount = item.discount ? item.discount : 0;
			//if (itemDiscount === 0) {
			itemDiscount = isPercentDiscount
				? Math.ceil((itemSubTotalAndModifier * valueDiscount) / 100)
				: 0;
			//}
			item.discount = itemDiscount;
		}

		return itemDiscount;
	}

	private static getDiscount(ticketDetail: TicketDetail) {
		let isPercentDiscount: boolean = true;
		let valueDiscount: number = 0;
		if (
			ticketDetail &&
			PosHelper.isArrayWithLength(ticketDetail.discounts)
		) {
			const discount: TicketDiscount = ticketDetail.discounts[0];
			if (discount) {
				isPercentDiscount = discount.id > 0;
				valueDiscount = discount.discountValue;
			}
		}
		return { isPercentDiscount, valueDiscount };
	}

	private static checkCanDiscountByDiscountNew(
		discounts: TicketDiscount[],
		item: TicketItem
	): number {
		let _canDiscount = true;
		let _discountValue = 0;
		let discount =
			discounts != null && discounts.some(x => x.isPercent === true) ? discounts.filter(x => x.isPercent === true)[0] : null;
		if (discount == null) return;
		if (discount.discountId == null) {
			//Discount của customer
			_canDiscount = item.canDiscount;
			_discountValue = discount.discountValue;
		} else {
			//Nếu discount yêu cầu áp dụng theo can discount của menuitem
			//thì lấy candiscount của menuitem
			if (
				discount.discount != null &&
				(discount.discount.isAllMenuItemCanDiscount)
			) {
				_canDiscount = item.canDiscount;
			}
			if (
				discount.discount != null &&
				discount.discount.isAll
			) {
				_canDiscount = discount.discount.isAll;
			}
			//Nếu không yêu cầu áp dụng theo menu item
			//thì chỉ cần kiểm tra thuộc danh sách menu item đưa vào
			else {
				if (
					discount != null &&
					discount.discount != null &&
					discount.discount.discountMenuItems != null
				) {
					let allowedItemsCanDiscount =
						discount.discount.discountMenuItems.find((d) => {
							return d.menuItemId == item.itemId;
						});
					if (allowedItemsCanDiscount != undefined) {
						_canDiscount = true;
					}
				}
			}
			_discountValue = discount.discount.discountValue;
		}
		item.canDiscount = _canDiscount;
		item.discount = _canDiscount
			? (_discountValue * item.price * item.quantity) / 100
			: 0;
		return _discountValue;
	}

	private static calculateTotalPriceAndModifiers(item: TicketItem) {
		let totalPriceModifiers: number = 0;
		if (PosHelper.isArrayWithLength(item.modifiers)) {
			item.modifiers.forEach((modifier) => {
				modifier.subTotal = 0;
				if (modifier.price && modifier.quantity) {
					modifier.subTotal = modifier.price * modifier.quantity;
					totalPriceModifiers += modifier.subTotal;
				}
			});
		}
		const itemTotal = item.subTotal + totalPriceModifiers;
		return itemTotal;
	}

	private static calculateSubTotalInCludeDiscountAndModifiers(
		item: TicketItem
	) {
		let totalPriceModifiers: number = 0;
		const itemDiscount = item.discount ? item.discount : 0;
		if (PosHelper.isArrayWithLength(item.modifiers)) {
			item.modifiers.forEach((modifier) => {
				modifier.subTotal = 0;
				if (modifier.price && modifier.quantity) {
					modifier.subTotal = modifier.price * modifier.quantity;
					totalPriceModifiers += modifier.subTotal;
				}
			});
		}
		const subTotal = item.subTotal - itemDiscount + totalPriceModifiers;
		return subTotal;
	}

	public static checkTicketGroupOrNot(
		tableDetail: TableDetailForOrder,
		floors: FloorInfo[]
	): boolean {
		let isTicketGrouped: boolean = false;
		if (tableDetail && tableDetail.hasTicket) {
			const tickets: TicketDetailForOrder[] = tableDetail.tickets;
			if (PosHelper.isArrayWithLength(floors)) {
				tickets.some((currentTicket) => {
					floors.some((floor) => {
						if (floor.tables) {
							floor.tables.some((table) => {
								if (table.id !== tableDetail.id) {
									if (table.tickets) {
										table.tickets.some((ticket) => {
											if (
												currentTicket.ticketId ===
												ticket.ticketId
											) {
												isTicketGrouped = true;
											}
											return isTicketGrouped;
										});
									}
								}
								return isTicketGrouped;
							});
						}
						return isTicketGrouped;
					});
					return isTicketGrouped;
				});
				return isTicketGrouped;
			}
		}
		return isTicketGrouped;
	}

	public static getAllGroupTicketIds(
		tableDetail: TableDetailForOrder,
		floors: FloorInfo[]
	): number[] {
		let tableIds: number[] = [tableDetail.id];
		if (tableDetail && tableDetail.hasTicket) {
			const tickets: TicketDetailForOrder[] = tableDetail.tickets;
			if (PosHelper.isArrayWithLength(floors)) {
				tickets.forEach((currentTicket) => {
					floors.forEach((floor) => {
						if (floor.tables) {
							floor.tables.forEach((table) => {
								if (table.id !== tableDetail.id) {
									if (table.tickets) {
										table.tickets.forEach((ticket) => {
											if (
												currentTicket.ticketId ===
												ticket.ticketId
											) {
												tableIds.push(table.id);
											}
										});
									}
								}
							});
						}
					});
				});
			}
		}
		return tableIds;
	}

	private static hasInCludeSpecials(item: TicketItem) {
		let isNoTax: boolean = false;
		let isNoServiceFee: boolean = false;
		let isNett: boolean = false;
		if (PosHelper.isArrayWithLength(item.modifiers)) {
			item.modifiers.forEach((modifier) => {
				if (!isNoTax)
					isNoTax = this.isEqualsName(modifier.name, "no tax");
				if (!isNoServiceFee)
					isNoServiceFee = this.isEqualsName(
						modifier.name,
						"no service fee"
					);
				if (!isNett) isNett = this.isEqualsName(modifier.name, "nett");
			});
		}
		return { isNoTax, isNoServiceFee, isNett };
	}

	private static isEqualsName(
		name: string = null,
		searchStr: string = null
	): boolean {
		return name ? name.toLowerCase().indexOf(searchStr) > -1 : false;
	}

	public static isAllItemsPrinted(ticketDetail: TicketDetail): boolean {
		let isAllItemsPrinted: boolean = true;
		if (ticketDetail && PosHelper.isArrayWithLength(ticketDetail.items)) {
			ticketDetail.items.some((x) => {
				if (!x.isPrintedToKitchen) {
					isAllItemsPrinted = false;
					return true;
				}
			});
		}
		return isAllItemsPrinted;
	}

	public static getItemIdsPrinted(ticketDetail: TicketDetail): number[] {
		let ids: number[] = [];
		if (ticketDetail && PosHelper.isArrayWithLength(ticketDetail.items)) {
			ticketDetail.items.forEach((x) => {
				if (x.isPrintedToKitchen) {
					ids.push(x.id);
				}
			});
		}
		return ids;
	}

	public static isModifierNoTax(modifier: Modifier): boolean {
		return this.isEqualsName(modifier.name, "no tax");
	}

	public static isModifierNoServiceFee(modifier: Modifier) {
		return this.isEqualsName(modifier.name, "no service fee");
	}

	public static isModifierNett(modifier: Modifier) {
		return this.isEqualsName(modifier.name, "nett");
	}

	public static getValueXModifierSpecials(item: TicketItem): number {
		const xModifierSpecials = [
			{
				nameVi: "x 1.5",
				nameEn: "x 1.5",
				nameCn: "中 ( T )",
				value: 1.5,
			},
			{
				nameVi: "x 2",
				nameEn: "x 2",
				nameCn: "大 ( L )",
				value: 2,
			},
			{
				nameVi: "x 3",
				nameEn: "x 3",
				nameCn: "雙 中 (2T)",
				value: 3,
			},
			{
				nameVi: "x 4",
				nameEn: "x 4",
				nameCn: "雙 大 (2L)",
				value: 4,
			},
		];
		let totalValueSpecial = 1;
		if (item && PosHelper.isArrayWithLength(item.modifiers)) {
			item.modifiers.forEach((modifier) => {
				let value = 1;
				xModifierSpecials.some((x) => {
					const isXModifierCn = this.isEqualsNameNoSpace(
						modifier.name,
						x.nameCn
					);
					if (isXModifierCn) {
						value = x.value;
						return true;
					}
					const isXModifierEn = this.isEqualsNameNoSpace(
						modifier.name,
						x.nameEn
					);
					if (isXModifierEn) {
						value = x.value;
						return true;
					}
					const isXModifierVi = this.isEqualsNameNoSpace(
						modifier.name,
						x.nameVi
					);
					if (isXModifierVi) {
						value = x.value;
						return true;
					}
				});
				totalValueSpecial = totalValueSpecial * value;
			});
		}
		return totalValueSpecial;
	}

	private static isEqualsNameNoSpace(
		name: string = null,
		searchStr: string = null
	): boolean {
		const nameNoSpace = this.removeSpaceString(name);
		const searchStrNoSpace = this.removeSpaceString(searchStr);
		return nameNoSpace === searchStrNoSpace;
	}

	private static removeSpaceString(str: string = null): string {
		return str ? str.split(" ").join("") : "";
	}
}
